// What the Brain is doing while it thinks (plan
// brain-conversacion-en-segundo-plano, 3.4).
//
// Until now the screen said «Pensando...» for ten seconds and then showed the
// answer. The steps already existed -- every tool call and model answer is kept
// on the run as it happens -- and this paints them: one row per tool, spinning
// while it runs, and what it thought, folded.

import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle2, ChevronRight, Lightbulb, Loader2 } from 'lucide-react'
import { FilaDesplegable } from '@/components/ui/fila-desplegable'
import { cn } from '@/lib/utils'
import type { BrainToolCall, RunEvent } from '@/services/brainApi'
import { rowsOf } from './brain-steps'
import { ToolCallRow } from './HerramientasUsadas'

const ICONS = {
  running: { Icon: Loader2, color: 'text-muted-foreground animate-spin' },
  done: { Icon: CheckCircle2, color: 'text-success' },
  failed: { Icon: AlertCircle, color: 'text-destructive' },
} as const

function Thought({ text }: { text: string }) {
  // Folded by default: it is the how, not the answer, and left open it pushes
  // the conversation down with every step.
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()
  return (
    <FilaDesplegable
      abierta={open}
      onAlternar={() => setOpen((v) => !v)}
      cabecera={
        <>
          <ChevronRight
            className={cn('h-3 w-3 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')}
          />
          <Lightbulb className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate text-xs text-muted-foreground">{t('brain.paso_penso')}</span>
        </>
      }
    >
      {open && (
        <div className="prose prose-sm max-w-none px-3 pb-2 text-xs text-muted-foreground dark:prose-invert">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
        </div>
      )}
    </FilaDesplegable>
  )
}

export function BrainSteps({
  events,
  names,
  calls,
}: {
  events: RunEvent[]
  /** Readable name by technical name. Without it the technical one is shown. */
  names?: Map<string, string>
  /** The calls of a finished answer, with what they were given and returned.
   * Each tool row takes the next one with its name and opens to its detail. */
  calls?: BrainToolCall[]
}) {
  const { t } = useTranslation()
  const rows = rowsOf(events)
  if (!rows.length) return null
  const unused = [...(calls ?? [])]

  return (
    <ol className="mt-2 space-y-0.5" aria-label={t('brain.pasos')}>
      {rows.map((row, i) => {
        if (row.kind === 'thought') {
          return (
            <li key={i}>
              <Thought text={row.text} />
            </li>
          )
        }
        const at = unused.findIndex((call) => call.tool === row.name)
        if (at !== -1) {
          const [call] = unused.splice(at, 1)
          return (
            <li key={i}>
              <ToolCallRow llamada={call} nombres={names} />
            </li>
          )
        }
        const { Icon, color } = ICONS[row.state]
        return (
          <li key={i} className="flex items-center gap-2 px-2 py-1">
            <span className="w-3 shrink-0" />
            <Icon className={cn('h-3.5 w-3.5 shrink-0', color)} />
            <span className="truncate text-xs">{names?.get(row.name) || row.name}</span>
          </li>
        )
      })}
    </ol>
  )
}

/** The steps of a finished answer, folded above it (3.5): the answer is what
 * is read, and how it got there is one click away. */
export function AnswerSteps(props: Parameters<typeof BrainSteps>[0]) {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()
  if (!rowsOf(props.events).length) return null
  return (
    <div className="mb-2">
      <FilaDesplegable
        abierta={open}
        onAlternar={() => setOpen((v) => !v)}
        cabecera={
          <>
            <ChevronRight
              className={cn('h-3 w-3 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')}
            />
            <span className="truncate text-xs text-muted-foreground">{t('brain.como_llego')}</span>
          </>
        }
      >
        {open && <BrainSteps {...props} />}
      </FilaDesplegable>
    </div>
  )
}
